import { memo, useEffect } from 'react'
import AppStore from '@v4/store/appStore'
import PropTypes from 'prop-types'
import PageData from '@v4/utils/data/PageData'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'

const Embedding = ({ children }) => {
	const { setAutoHeight, setParentWindowHeight } = AppStore.useStoreActions(actions => actions.general)
	const questionId = AppStore.useStoreState(state => state.question.data?.id)
	const pageType = PageBuilderSelector.useType()
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const widgetKey = AppStore.useStoreState(state => state.general.widgetKey)

	useEffect(() => {
		if (!window || !document || window === window.parent || isBuilderMode) {
			return undefined
		}

		document.body.classList.add('embedded')

		let timeout = null
		let timeout2 = null
		let timeout3 = null

		const handleResize = () => {
			clearTimeout(timeout)
			clearTimeout(timeout2)
			const isTypeQuestions = PageData.isTypeQuestions(pageType)
			const callback = () => {
				document.body.style.height = isTypeQuestions ? '600px' : '0px'
				document.body.style.flexGrow = '0'

				window.parent.postMessage(
					{
						event: 'scoreapp.resize',
						scoreapp: {
							height: document.body.scrollHeight,
							type: pageType,
						},
						key: widgetKey,
					},
					'*',
				)
				document.body.style.height = null
				document.body.style.flexGrow = null
				document.body.style.width = null
			}
			timeout = setTimeout(() => {
				callback()
				timeout2 = setTimeout(() => {
					callback()
				}, 40)
				timeout3 = setTimeout(() => {
					callback()
				}, 100)
			}, 0)
		}

		handleResize()

		window.addEventListener('resize', handleResize)
		window.addEventListener('lazy-loaded', handleResize)
		window.addEventListener('header-updated', handleResize)
		window.addEventListener('image-loaded', handleResize)

		return () => {
			clearTimeout(timeout)
			clearTimeout(timeout2)
			clearTimeout(timeout3)
			window.removeEventListener('resize', handleResize)
			window.removeEventListener('lazy-loaded', handleResize)
			window.removeEventListener('header-updated', handleResize)
			window.removeEventListener('image-loaded', handleResize)
		}
	}, [isBuilderMode, pageType, questionId, widgetKey])

	useEffect(() => {
		if (!window || window === window.parent) {
			return undefined
		}

		window.parent.postMessage(
			{
				event: 'scoreapp.load',
				scoreapp: {
					height: document.body.scrollHeight,
					type: pageType,
				},
				key: widgetKey,
			},
			'*',
		)

		const handleMessage = event => {
			try {
				const scoreAppData = event.data.scoreapp
				if (!scoreAppData || !(!event.data.key || event.data.key === widgetKey)) {
					return
				}
				if (scoreAppData.autoHeight) {
					setAutoHeight(true)
				}

				setParentWindowHeight(scoreAppData.windowHeight)
				// eslint-disable-next-line no-empty
			} catch (e) {}
		}
		window.addEventListener('message', handleMessage)

		return () => {
			window.removeEventListener('message', handleMessage)
		}
	}, [pageType, setAutoHeight, setParentWindowHeight, widgetKey])

	return children || null
}

Embedding.propTypes = {
	children: PropTypes.node,
}

export default memo(Embedding)
